import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { AnalyzeCommand, AnalyzeCommandResult, ConvertCommand, ConvertCommandResult, ConvertRoiCommand, ConvertRoiResult, ExportRoiCommand, ExportRoiResult, FileSelection, NamedBlob, AnonymizationResult, AnonymizationCommand, AnonymizationOptions, SetTagsResult, SetTagsCommand, NiftiToDicomCommand, VoiToRoiCommand, VoiToRoiResult, MergeTagsCommand, MergeTagsResult, ExtractPatientModuleCommand, ExtractPatientModuleResult, FormatDynamicString, FormatDynamicStringResult } from './dicom-common';
import { DicomTagContents } from './dicom-writer/dicom-vr';

export const BASE_URL = new InjectionToken("BASE_URL");
export const DICOM_WORKER = new InjectionToken("DICOM_WORKER");
export type FormatDynamicStringCb = (formatString: string, regionIndex: number) => string;

@Injectable({
  providedIn: 'root'
})
export class DicomService implements OnDestroy {
  private worker?: Worker;

  private convertResolve: ((result: ConvertCommandResult) => void)[] = [];
  private convertReject: ((err: Error) => void)[] = [];

  private analyzeResolve: ((result: DicomAnalysisResult) => void)[] = [];
  private analyzeReject: ((err: Error) => void)[] = [];

  private exportRoiResolve: ((result: ExportRoiResult) => void)[] = [];
  private exportRoiReject: ((err: Error) => void)[] = [];

  private convertRoiResolve: ((result: ArrayBuffer) => void)[] = [];
  private convertRoiReject: ((err: Error) => void)[] = [];

  private anonymizeResolve: ((result: NamedBlob[]) => void)[] = [];
  private anonymizeReject: ((err: Error) => void)[] = [];

  private setDicomTagsResolve: ((result: NamedBlob[]) => void)[] = [];
  private setDicomTagsReject: ((err: Error) => void)[] = [];

  private niftiToDicomResolve: ((result: NamedBlob[]) => void)[] = [];
  private niftiToDicomReject: ((err: Error) => void)[] = [];

  private niftiToRtstructResolve: ((result: Blob) => void)[] = [];
  private niftiToRtstructReject: ((err: Error) => void)[] = [];

  private mergeTagsResolve: ((result: NamedBlob[]) => void)[] = [];
  private mergeTagsReject: ((err: Error) => void)[] = [];

  private exportPatientModuleResolve: ((result: ArrayBuffer) => void)[] = [];
  private exportPatientModuleReject: ((err: Error) => void)[] = [];

  private formatDynamicStringCb: (FormatDynamicStringCb | null)[] = [];

  private readonly initializationComplete: Promise<void>;

  constructor(@Inject(BASE_URL) baseUrl: string, @Inject(DICOM_WORKER) worker?: () => Worker) {
    if (globalThis.Worker) {
      this.worker = worker();//new Worker(dicomWorkerUrl, { type: 'module', name: 'dicom' });
      this.worker.onmessage = msg => this.onWorkerMessage(msg);
      this.initializationComplete = Promise.resolve();
    } else {
      this.initializationComplete = import('worker_threads')
      .then(wt => {
        const worker = new wt.Worker(`${baseUrl}/dicom.worker-node.js`);
        worker.addListener("message", msg => this.onWorkerMessage(msg));
        this.worker = worker as unknown as Worker;
      });
    }
  }

  // Only needed in Node.js environment to wait for worker initialization
  public async waitForInitialization(): Promise<void> {
    await this.initializationComplete;
  }

  ngOnDestroy(): void {
    this.worker?.terminate();
  }

  public convertToNifti(dicomFiles: NamedBlob[] | FileSelection, outputName: string, normalizeByValue?: number): Promise<ConvertCommandResult> {

    return new Promise<ConvertCommandResult>((resolve, reject) => {
      this.convertResolve.push(resolve);
      this.convertReject.push(reject);

      let msg: ConvertCommand = {
        command: "convert",
        inputFiles: dicomFiles,
        outputName,
        normalizeByValue,
      };

      this.worker.postMessage(msg);
    });
  }

  public convertRoiToNifti(dicomFile: Blob, roiNumber: number): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      this.convertRoiResolve.push(resolve);
      this.convertRoiReject.push(reject);

      let msg: ConvertRoiCommand = {
        command: "convert-roi",
        file: dicomFile,
        roiNumber,
      };

      this.worker.postMessage(msg);
    });
  }

  public analyzeFiles(files: NamedBlob[] | string): Promise<DicomAnalysisResult> {

    return new Promise<DicomAnalysisResult>((resolve, reject) => {
      this.analyzeResolve.push(resolve);
      this.analyzeReject.push(reject);

      let msg: AnalyzeCommand = {
        command: "analyze",
        files,
      };

      this.worker.postMessage(msg);
    });
  }

  public exportRoi(file: Blob, roiNumber: number): Promise<ExportRoiResult> {
    return new Promise<ExportRoiResult>((resolve, reject) => {
      this.exportRoiResolve.push(resolve);
      this.exportRoiReject.push(reject);

      let msg: ExportRoiCommand = {
        command: "export-roi",
        file,
        roiNumber,
      };

      this.worker.postMessage(msg);
    });
  }

  public anonymizeDicoms(files: NamedBlob[] | FileSelection, options: AnonymizationOptions): Promise<NamedBlob[]> {
    return new Promise<NamedBlob[]>((resolve, reject) => {
      this.anonymizeResolve.push(resolve);
      this.anonymizeReject.push(reject);

      let msg: AnonymizationCommand = {
        command: "anonymize",
        files,
        options,
      };

      this.worker.postMessage(msg);
    });
  }

  public deanonymizeDicoms(files: NamedBlob[] | FileSelection, originalPatientModuleData: ArrayBuffer): Promise<NamedBlob[]> {
    return new Promise<NamedBlob[]>((resolve, reject) => {
      this.mergeTagsResolve.push(resolve);
      this.mergeTagsReject.push(reject);

      let msg: MergeTagsCommand = {
        command: "merge-tags",
        files,
        mergeWith: originalPatientModuleData,
      };

      this.worker.postMessage(msg);
    });
  }

  public extractPatientModuleData(file: ArrayBuffer): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      this.exportPatientModuleResolve.push(resolve);
      this.exportPatientModuleReject.push(reject);

      let msg: ExtractPatientModuleCommand = {
        command: "extract-patient-module",
        file,
      };

      this.worker.postMessage(msg);
    });
  }

  public setDicomTags(files: NamedBlob[], tags: any[]): Promise<NamedBlob[]> {
    return new Promise<NamedBlob[]>((resolve, reject) => {
      this.setDicomTagsResolve.push(resolve);
      this.setDicomTagsReject.push(reject);
      let msg: SetTagsCommand = {
        command: "set-tags",
        files,
        tags,
      };
      this.worker.postMessage(msg);
    });
  }

  public niftiToDicom(file: NamedBlob | string, modality: string, seriesDescription: string, tags: any[],
    splitVoi = false, binaryTags?: ArrayBuffer, voxelLabels = false, formatString?: FormatDynamicStringCb): Promise<NamedBlob[]> {

    if (splitVoi && voxelLabels) {
      return Promise.reject(new Error("Cannot use splitVoi and voxelLabels at the same time"));
    }

    return new Promise<NamedBlob[]>((resolve, reject) => {
      this.formatDynamicStringCb.push(formatString ?? null);
      this.niftiToDicomResolve.push(resolve);
      this.niftiToDicomReject.push(reject);

      let msg: NiftiToDicomCommand = {
        command: "nifti-to-dicom",
        file,
        tags,
        modality,
        seriesDescription,
        splitVoi,
        binaryTags,
        voxelLabels,
      };

      this.worker.postMessage(msg);
    });
  }

  // segmentColor is an RGB value, elements are between 0.0 and 1.0
  public niftiToRtstruct(file: NamedBlob | string, originalPatientMatrix: string, referencedSopClassUID: string,
    referencedInstanceUIDs: string[], segmentName: string, segmentColor: number[], frameOfReferenceUID: string,
    tags: DicomTagContents[], binaryTags?: ArrayBuffer, voxelLabels = false, formatString?: FormatDynamicStringCb): Promise<Blob> {

    this.formatDynamicStringCb.push(formatString ?? null);

    return new Promise<Blob>((resolve, reject) => {
      this.niftiToRtstructResolve.push(resolve);
      this.niftiToRtstructReject.push(reject);

      let msg: VoiToRoiCommand = {
        command: "voi-to-roi",
        file,
        patientMatrix: originalPatientMatrix,
        referencedSopClassUID,
        referencedInstanceUIDs,
        segmentName,
        segmentColor,
        frameOfReferenceUID,
        tags,
        binaryTags,
        voxelLabels,
      };

      this.worker.postMessage(msg);
    });
  }

  private onWorkerMessage(event: MessageEvent<any>) {
    switch (event.data.command) {
      case "convert": {
        let e = <ConvertCommandResult> event.data;

        let resolve = this.convertResolve.shift();
        let reject = this.convertReject.shift();

        if (e.exitCode === 0) {
          resolve(e);
        } else {
          reject(new Error(`Failed with exit code ${e.exitCode}`));
        }
        break;
      }
      case "analyze": {
        let e = <AnalyzeCommandResult> event.data;

        let resolve = this.analyzeResolve.shift();
        let reject = this.analyzeReject.shift();

        if (e.jsonResult) {
          resolve(JSON.parse(e.jsonResult));
        } else {
          reject(new Error(`Analyzer failure`));
        }
        break;
      }
      case "export-roi": {
        let e = event.data as ExportRoiResult;

        let resolve = this.exportRoiResolve.shift();
        let reject = this.exportRoiReject.shift();

        if (e.contours !== null) {
          resolve(e);
        } else {
          reject(new Error("Incorrect ROI number or missing contour data"));
        }

        break;
      }
      case "convert-roi": {
        let e = event.data as ConvertRoiResult;

        let resolve = this.convertRoiResolve.shift();
        let reject = this.convertRoiReject.shift();

        if (e.fileData) {
          resolve(e.fileData);
        } else {
          reject(new Error("DICOM-RT ROI conversion failed"));
        }
        break;
      }
      case "anonymize": {
        let e = event.data as AnonymizationResult;
        let resolve = this.anonymizeResolve.shift();
        let reject = this.anonymizeReject.shift();

        if (e.error) {
          reject(new Error("Anonymization failed"));
        } else {
          resolve(e.files);
        }
        break;
      }
      case "set-tags": {
        let e = event.data as SetTagsResult;
        const resolve = this.setDicomTagsResolve.shift();
        const reject = this.setDicomTagsReject.shift();

        if (e.error) {
          reject(new Error("Setting DICOM tags failed"));
        } else {
          resolve(e.files);
        }
        break;
      }
      case "nifti-to-dicom": {
        let e = event.data as SetTagsResult;
        const resolve = this.niftiToDicomResolve.shift();
        const reject = this.niftiToDicomReject.shift();
        this.formatDynamicStringCb.shift();

        if (e.error) {
          reject(new Error("NIfTI to DICOM conversion failed"));
        } else {
          resolve(e.files);
        }
        break;
      }

      case "voi-to-roi": {
        let e = event.data as VoiToRoiResult;
        const resolve = this.niftiToRtstructResolve.shift();
        const reject = this.niftiToRtstructReject.shift();
        this.formatDynamicStringCb.shift();

        if (e.file) {
          resolve(e.file);
        } else {
          reject(new Error("NIfTI to RTSTRUCT conversion failed"));
        }
        break;
      }

      case "merge-tags": {
        let e = event.data as MergeTagsResult;
        let resolve = this.mergeTagsResolve.shift();
        let reject = this.mergeTagsReject.shift();

        if (e.error) {
          reject(new Error("Merging DICOM tags failed"));
        } else {
          resolve(e.files);
        }
        break;
      }

      case "extract-patient-module": {
        let e = event.data as ExtractPatientModuleResult;
        let resolve = this.exportPatientModuleResolve.shift();
        let reject = this.exportPatientModuleReject.shift();

        if (e.error || !e.patientModuleData) {
          reject(new Error("Extracting patient module data failed"));
        } else {
          resolve(e.patientModuleData);
        }
        break;
      }

      case "format-dynamic-string": {
        const e = event.data as FormatDynamicString;
        const cb = this.formatDynamicStringCb[0];
        let result = e.formatString;

        if (cb) {
          try {
            result = cb(e.formatString, e.index);
          } catch (err) {
            console.error("Error in formatDynamicString callback:", err);
          }
        } else {
          console.warn("No formatDynamicString callback provided to handle: " + e.formatString);
        }

        this.worker.postMessage({
          command: "format-dynamic-string",
          resultString: result,
        } as FormatDynamicStringResult);
        break;
      }
    }
  }
}

export interface DicomAnalysisResult {
  patients: DicomAnalysisResultPatient[];
}

export interface DicomAnalysisResultPatient {
  birthDate: string;
  id: string;
  name: string;
  issuerOfPatientId: string;
  sex: "M" | "F"; // FIXME: This value is not 100% standardized, also expect "Weiblich/Mannlich" etc.
  studies: DicomAnalysisResultStudy[];
}

export interface DicomAnalysisResultStudy {
  date: string;
  description: string;
  id: string;
  time: string;
  uid: string;
  series: DicomAnalysisResultSerie[];
  patientAge?: string;
  patientWeight?: number;
  patientSize?: number;
  referringPhysician?: string;
  institutionName?: string;
}

export interface DicomAnalysisResultSerie {
  description: string;
  filenames: string[];
  modality: string;
  uid: string;
  units?: string;

  imageType: string;
  correctedImage: string;
  repetitionTime?: number;
  echoTime?: number;
  hasMriDiffusionSeq?: boolean;

  radionuclide?: string; // Code meaning
  radionuclideCode?: string; // Code value
  radionuclideDose?: number;
  radionuclideHalfLife?: number;

  radiopharmaceutical?: string; // Code meaning
  radiopharmaceuticalCode?: string; // Code value

  radiopharmaceuticalStartTime?: string;
  seriesTime?: string;

  // If modality === "RTSTRUCT"
  rois?: DicomRoi[];

  imageAttributes?: DicomImageAttributes;
  mediaStorageType?: "CTImageStorage" | "PositronEmissionTomographyImageStorage" | "MRImageStorage" | string;
}

export interface DicomRoi {
  number: number;
  name: string;
  color: string; // RGB 0-255, separated by '\'
}

export interface DicomImageAttributes {
  // Voxel count
  dimensions: number[];

  // Voxel size
  spacing: number[];

  // The above [x,y,z] information corresponds to:
  // x: left/right
  // y: anterior/posterior
  // z: superior/inferior
}
